import { LucideFolderOpen, LucideLoader2, LucideTrendingUp, LucideZap } from "lucide-react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";

import { Container, Stack, Heading } from "@/components/ui";
import { useDeleteRoadmapItem } from "@/domains/admin/roadmap/hooks/useDeleteRoadmapItem";
import { type Roadmap_Item } from "@/domains/admin/roadmap/types/roadmap-types";

import { useRoadmapFetch, useFilteredRoadmap } from "../../hooks";
import { EditModal, StatusBar } from "./components/molecules";
import { RoadmapItemCardTracQMemo as RoadmapItemCardTracQ } from "./components/molecules/RoadmapItemCardTracQ";

/* ───────────────────────────────────────────────────────────── */

/**
 * WHAT:
 *   Gets score value for impact/effort calculation.
 *
 * WHY:
 *   Converts string scores to numeric values for ROI calculation.
 *
 * @param {string} [score] - Score string (low, medium, high)
 *
 * @returns {number} Numeric score value
 *
 */
const getScoreValue = (score?: string): number => {
  switch (score) {
    case "low": {
      return 1;
    }
    case "medium": {
      return 2;
    }
    case "high": {
      return 3;
    }
    default: {
      return 0;
    }
  }
};

/**
 * WHAT:
 *   Roadmap Overview Screen - Main entry point for the roadmap management interface.
 *
 * WHY:
 *   Shows top ROI items and key metrics for quick decision making.
 *
 * BEHAVIOR:
 *   - Displays top ROI items
 *   - Shows module statistics
 *   - Provides keyboard navigation
 *   - Handles loading, error, and empty states
 *
 * @returns {JSX.Element} Roadmap overview screen component
 *
 * @example
 * ```tsx
 * <OverviewScreen />
 * ```
 *
 */
export const OverviewScreen = () => {
  /* ---------- Data Layer ---------- */
  const items = useFilteredRoadmap();
  const { error, loading } = useRoadmapFetch();
  const { mutate: deleteItem } = useDeleteRoadmapItem();

  /* ---------- UI State Management ---------- */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editItem, setEditItem] = useState<null | Roadmap_Item>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [focusedCardIndex, setFocusedCardIndex] = useState<number>(-1);

  /* ---------- Refs for Keyboard Navigation ---------- */
  const searchInputReference = useRef<HTMLInputElement>(null);
  const cardReferences = useRef<(HTMLDivElement | null)[]>([]);

  /* ---------- Animation State ---------- */
  useEffect(() => {
    if (!loading && items.length > 0) {
      // Trigger staggered animation after content loads
      const timer = setTimeout(() => {
        setIsLoaded(true);
      }, 100);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [loading, items.length]);

  /* ---------- Keyboard Navigation & Shortcuts ---------- */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Global keyboard shortcuts - Support both Mac (Cmd) and Windows (Ctrl)
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey) {
        switch (event.key.toLowerCase()) {
          case "n": {
            event.preventDefault();
            setShowCreateModal(true);
            break;
          }
          case "f": {
            event.preventDefault();
            searchInputReference.current?.focus();
            break;
          }
          case "escape": {
            event.preventDefault();
            setShowCreateModal(false);
            setEditItem(null);
            break;
          }
        }
      }

      // Arrow key navigation between cards
      if (focusedCardIndex >= 0 && items.length > 0) {
        switch (event.key) {
          case "ArrowRight": {
            event.preventDefault();
            setFocusedCardIndex((previous) => Math.min(previous + 1, items.length - 1));
            break;
          }
          case "ArrowLeft": {
            event.preventDefault();
            setFocusedCardIndex((previous) => Math.max(previous - 1, 0));
            break;
          }
          case "ArrowDown": {
            event.preventDefault();
            setFocusedCardIndex((previous) => Math.min(previous + 3, items.length - 1));
            break;
          }
          case "ArrowUp": {
            event.preventDefault();
            setFocusedCardIndex((previous) => Math.max(previous - 3, 0));
            break;
          }
          case "Home": {
            event.preventDefault();
            setFocusedCardIndex(0);
            break;
          }
          case "End": {
            event.preventDefault();
            setFocusedCardIndex(items.length - 1);
            break;
          }
          case "Enter":
          case " ": {
            event.preventDefault();
            if (focusedCardIndex >= 0 && items[focusedCardIndex]) {
              setEditItem(items[focusedCardIndex]);
            }
            break;
          }
        }
      }
    },
    [focusedCardIndex, items],
  );

  // Focus management for keyboard navigation
  useEffect(() => {
    if (focusedCardIndex >= 0 && cardReferences.current[focusedCardIndex]) {
      cardReferences.current[focusedCardIndex].focus();
    }
  }, [focusedCardIndex]);

  // Global keyboard event listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  /* ---------- Event Handlers ---------- */
  const handleEdit = useCallback((item: Roadmap_Item) => {
    setEditItem(item);
  }, []);

  const handleDelete = useCallback(
    (item: Roadmap_Item) => {
      if (globalThis.confirm(`"${item.title}" wirklich löschen?`)) {
        deleteItem(item.id);
      }
    },
    [deleteItem],
  );

  /* ---------- Data Processing ---------- */
  /**
   * WHAT:
   *   Calculates ROI for a roadmap item.
   *
   * WHY:
   *   ROI = Impact / Effort, used to prioritize items.
   *
   * @param {Roadmap_Item} item - Roadmap item
   *
   * @returns {number} ROI value
   *
   */
  const calculateRoi = useCallback((item: Roadmap_Item): number => {
    const impact = getScoreValue(item.impact_score);
    const effort = getScoreValue(item.effort_estimate);

    // ROI = Impact / Effort (avoid division by zero)
    return effort > 0 ? impact / effort : impact;
  }, []);

  // Calculate ROI for each item and get top items
  const topRoiItems = useMemo(() => {
    type ItemWithRoi = Roadmap_Item & { roi: number };
    const itemsWithRoi: ItemWithRoi[] = items.map((item) => ({
      ...item,
      roi: calculateRoi(item),
    }));
    // Reason: TypeScript has difficulty inferring types for toSorted with complex generics

    const sorted = itemsWithRoi.toSorted((a, b) => b.roi - a.roi);
    return sorted.slice(0, 3); // Top 3 items
  }, [calculateRoi, items]);

  // Group items by module for module overview
  const moduleStats = useMemo(() => {
    const stats: Record<
      string,
      { count: number; highRoiCount: number; name: string; totalRoi: number }
    > = {};

    for (const item of items) {
      const moduleStat = (stats[item.module] ??= {
        count: 0,
        highRoiCount: 0,
        name: item.module,
        totalRoi: 0,
      });

      moduleStat.count++;

      // Calculate ROI for this item
      const impact = getScoreValue(item.impact_score);
      const effort = getScoreValue(item.effort_estimate);
      const roi = effort > 0 ? impact / effort : impact;

      moduleStat.totalRoi += roi;
      if (roi >= 2) {
        moduleStat.highRoiCount++;
      }
    }

    return stats;
  }, [items]);

  /* ---------- UI Components ----------

  /**
   * WHAT:
   *   Loading State Component - Displays a centered loading spinner with descriptive text.
   *
   * @returns {JSX.Element} Loading state component
   *
   */
  const LoadingState = () => (
    <div aria-live="polite" className="flex items-center justify-center py-12" role="status">
      <div className="text-center space-y-4">
        <LucideLoader2
          aria-hidden="true"
          className="w-8 h-8 text-tracq-bright-blue animate-spin mx-auto"
        />
        <p className="text-slate-300 font-medium">Loading roadmap items...</p>
      </div>
    </div>
  );

  /**
   * WHAT:
   *   Error State Component - Displays error messages in a branded error container.
   *
   * @param {object} props - Component props
   *
   * @param {string} props.message - Error message to display
   *
   * @returns {JSX.Element} Error state component
   *
   */
  const ErrorState = ({ message }: { message: string }) => (
    <div
      aria-live="assertive"
      className="bg-red-500/10 border border-red-500 rounded-tracq p-6"
      role="alert"
    >
      <p className="text-red-400 font-medium">Error: {message}</p>
    </div>
  );

  /**
   * WHAT:
   *   Empty State Component - Displays when no roadmap items are found.
   *
   * WHY:
   *   Provides guidance for creating the first item.
   *
   * @returns {JSX.Element} Empty state component
   *
   */
  const EmptyState = () => (
    <div aria-live="polite" className="text-center py-12 space-y-4" role="status">
      <LucideFolderOpen
        aria-hidden="true"
        className="w-16 h-16 text-slate-500 mx-auto animate-bounce"
      />
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-slate-200">No roadmap items found</h3>
        <p className="text-slate-400 max-w-md mx-auto">
          Get started by creating your first roadmap item. Click the &quot;New Item&quot; button
          above to begin.
        </p>
      </div>
    </div>
  );

  /**
   * WHAT:
   *   Top ROI Items Section - Shows the highest ROI items in a horizontal row.
   *
   * @returns {JSX.Element} Top ROI section component
   *
   */
  const TopRoiSection = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <LucideTrendingUp className="w-6 h-6 text-green-500" />
        <h2 className="text-xl font-semibold text-white">Top ROI Items</h2>
        <span className="text-sm text-slate-400">Highest Impact vs Effort ratio</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {topRoiItems.map((item, index) => {
          const roadmapItem: Roadmap_Item = item;
          return (
            <div
              aria-label={`Top ROI item: ${roadmapItem.title}`}
              className={`transition-all duration-500 ease-out hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:ring-offset-2 focus:ring-offset-slate-900 ${
                isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              key={roadmapItem.id}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setEditItem(roadmapItem);
                }
              }}
              ref={(element) => {
                cardReferences.current[index] = element;
              }}
              role="listitem"
              style={{ transitionDelay: `${String(index * 100)}ms` }}
              tabIndex={0}
            >
              <RoadmapItemCardTracQ
                item={roadmapItem}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  /**
   * WHAT:
   *   Module Overview Section - Shows module statistics in a compact row.
   *
   * @returns {JSX.Element} Module overview section component
   *
   */
  const ModuleOverviewSection = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <LucideZap className="w-6 h-6 text-tracq-deep-orange" />
        <h2 className="text-xl font-semibold text-white">Module Overview</h2>
        <span className="text-sm text-slate-400">Quick stats by module</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {Object.values(moduleStats).map((module, index) => (
          <div
            className={`p-6 rounded-tracq transition-all duration-300 flex flex-col justify-between min-h-[200px] group hover:scale-[1.02] shadow-tracq-card bg-slate-700/30 border border-slate-600/50 hover:border-slate-500/50 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
            key={module.name}
            style={{ transitionDelay: `${String((index + 6) * 50)}ms` }}
          >
            {/* Header Section */}
            <div className="space-y-4">
              {/* Module Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-tracq-deep-orange to-tracq-light-orange rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {module.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-wide text-slate-300">
                    {module.name}
                  </span>
                </div>
                <span className="text-xs font-medium text-slate-400 bg-slate-600 px-3 py-1.5 rounded-full group-hover:bg-slate-500 transition-colors duration-200">
                  {module.count} {module.count === 1 ? "item" : "items"}
                </span>
              </div>

              {/* Module Title */}
              <h3 className="text-white font-semibold text-lg leading-tight group-hover:text-tracq-deep-orange transition-colors duration-200">
                {module.name} Module
              </h3>

              {/* Module Description */}
              <p className="text-sm text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-200">
                Module statistics and performance metrics
              </p>
            </div>

            {/* Stats Section */}
            <div className="space-y-3 mt-4">
              {/* High ROI Items */}
              <div className="flex items-center justify-between p-3 bg-slate-600/50 rounded-lg group-hover:bg-slate-600 transition-colors duration-200">
                <div className="flex items-center gap-2">
                  <LucideTrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-slate-300">High ROI Items</span>
                </div>
                <span className="text-green-500 font-semibold">{module.highRoiCount}</span>
              </div>

              {/* Average ROI */}
              <div className="flex items-center justify-between p-3 bg-slate-600/50 rounded-lg group-hover:bg-slate-600 transition-colors duration-200">
                <div className="flex items-center gap-2">
                  <LucideZap className="w-4 h-4 text-tracq-bright-blue" />
                  <span className="text-sm text-slate-300">Avg ROI</span>
                </div>
                <span className="text-tracq-bright-blue font-semibold">
                  {(module.totalRoi / module.count).toFixed(1)}
                </span>
              </div>
            </div>

            {/* Footer Section */}
            <div className="flex items-center justify-between pt-4 mt-auto border-t border-slate-600 group-hover:border-slate-500 transition-colors duration-200">
              <span className="text-xs text-slate-500 font-medium group-hover:text-slate-400 transition-colors duration-200">
                Module Stats
              </span>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                <div className="w-2 h-2 bg-tracq-bright-blue rounded-full" />
                <div className="w-2 h-2 bg-tracq-deep-orange rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  /* ---------- Main Render ---------- */
  return (
    <Container className="text-white" maxWidth="full" padding="lg">
      <Stack aria-label="Roadmap content" direction="col" gap={8}>
        {/* Roadmap Overview Section */}
        <Stack aria-label="Roadmap statistics" direction="col" gap={6}>
          <Stack align="center" direction="row" gap={3}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              Roadmap Overview
            </Heading>
          </Stack>
          <StatusBar />
        </Stack>

        {/* Loading State */}
        {loading ? <LoadingState /> : null}

        {/* Error State */}
        {error ? <ErrorState message={error} /> : null}

        {/* Content Rendering */}
        {!loading && !error && (
          <>
            {/* Module Overview */}
            {Object.keys(moduleStats).length > 0 && <ModuleOverviewSection />}

            {/* Top ROI Items */}
            {topRoiItems.length > 0 ? <TopRoiSection /> : null}

            {/* Empty State */}
            {items.length === 0 && <EmptyState />}
          </>
        )}

        {/* Modal Management */}
        {showCreateModal ? (
          <EditModal
            onClose={() => {
              setShowCreateModal(false);
            }}
          />
        ) : null}
        {editItem ? (
          <EditModal
            item={editItem}
            onClose={() => {
              setEditItem(null);
            }}
          />
        ) : null}
      </Stack>
    </Container>
  );
};
